import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { FONT_WEIGHT, MOBILE_SCREEN, typographyStyles } from "../theme";

/*
 * Paragraph / body-text components — the source of truth for body/prose styling.
 * Each emits its `typographyStyles.body*` token, then `textBase` (margin reset),
 * then `textOverrides` last, so a passed prop wins by source order (no `&&`) and
 * unset props fall through to the token. Colour isn't defaulted (text inherits
 * COLOR_APP_TEXT from :root). Reuse a style on another element via `as`
 * (e.g. `<Paragraph as="span">`).
 */

interface TextProps {
  marginTop?: string;
  marginBottom?: string;
  textAlign?: React.CSSProperties["textAlign"];
  marginLeft?: string;
  marginRight?: string;
  color?: string;
  fontWeight?: number;
  opacity?: number;
}

// Reset the UA paragraph margin so spacing is layout-driven.
const textBase = `
  margin: 0;
`;

// Emitted last so a passed prop wins; each declaration emits only when set.
const textOverrides = ({
  color,
  textAlign,
  fontWeight,
  opacity,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
}: TextProps) => `
  ${color != null ? `color: ${color};` : ""}
  ${textAlign != null ? `text-align: ${textAlign};` : ""}
  ${fontWeight != null ? `font-weight: ${fontWeight};` : ""}
  ${opacity != null ? `opacity: ${opacity};` : ""}
  ${marginTop != null ? `margin-top: ${marginTop};` : ""}
  ${marginBottom != null ? `margin-bottom: ${marginBottom};` : ""}
  ${marginLeft != null ? `margin-left: ${marginLeft};` : ""}
  ${marginRight != null ? `margin-right: ${marginRight};` : ""}
`;

// Scene body scales change mobile weight, so explicit weight props must win at that breakpoint too.
const responsiveTextOverrides = (props: TextProps) => css`
  ${textOverrides(props)}
  ${props.fontWeight != null &&
  css`
    ${MOBILE_SCREEN} {
      font-weight: ${props.fontWeight};
    }
  `}
`;

// --- Body (regular weight) --------------------------------------------------

/** Default body copy — Figma "Body Medium" (15px / 300 / 160%). */
export const Paragraph = styled.p<TextProps>`
  ${typographyStyles.bodyMedium}
  ${textBase}
  ${textOverrides}
`;

/** Figma "Body Extra-Small" (11px / 300 / 140%). */
export const ParagraphXSmall = styled.p<TextProps>`
  ${typographyStyles.bodyXSmall}
  ${textBase}
  ${textOverrides}
`;

/** Figma "Body Small" (14px / 300 / 140%) — one step below the default `Paragraph`. */
export const ParagraphSmall = styled.p<TextProps>`
  ${typographyStyles.bodySmall}
  ${textBase}
  ${textOverrides}
`;

/** Explicit-name alias of `Paragraph` (Figma "Body Medium", 15px). */
export const ParagraphMedium = styled(Paragraph)``;

/** Figma "Body Large" (22px / 300 / 160%). */
export const ParagraphLarge = styled.p<TextProps>`
  ${typographyStyles.bodyLarge}
  ${textBase}
  ${textOverrides}
`;

/** Semantic alias of `ParagraphLarge` for call sites that mean "extra-large body". */
export const ParagraphXLarge = styled(ParagraphLarge)``;

// --- Body Semibold (header-styled paragraphs) -------------------------------

/** Semibold body copy — Figma "Body Medium Semibold" (15px / 600 / 150%). */
export const ParagraphHeader = styled.p<TextProps>`
  ${typographyStyles.bodyMediumSemibold}
  ${textBase}
  ${textOverrides}
`;

/** Figma "Body Small Semibold" (14px / 600 / 20px / -0.01em). */
export const ParagraphHeaderSmall = styled.p<TextProps>`
  ${typographyStyles.bodySmallSemibold}
  ${textBase}
  ${textOverrides}
`;

/** No direct token; uses Body XSmall at semibold weight. */
export const ParagraphHeaderXSmall = styled.p<TextProps>`
  ${typographyStyles.bodyXSmall}
  font-weight: ${FONT_WEIGHT.semibold};
  ${textBase}
  ${textOverrides}
`;

/** Figma "Body Large Semibold" (22px). */
export const ParagraphHeaderLarge = styled.p<TextProps>`
  ${typographyStyles.bodyLargeSemibold}
  ${textBase}
  ${textOverrides}
`;

/** Semantic alias of `ParagraphHeaderLarge` — see `ParagraphXLarge`. */
export const ParagraphHeaderXLarge = styled(ParagraphHeaderLarge)``;

/** Completion introduction — authored 18/14px semibold scale. */
export const CompletionDescription = styled.p<TextProps>`
  ${typographyStyles.completionDescription}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Completion benefits — authored 14/12px semibold scale. */
export const CompletionBenefitText = styled.p<TextProps>`
  ${typographyStyles.completionBenefit}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Compact referral navigation label. */
export const ReferralNavigationLabel = styled.p<TextProps>`
  ${typographyStyles.referralNavigationLabel}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Referral introduction — authored desktop semibold/mobile medium scale. */
export const ReferralIntro = styled.p<TextProps>`
  ${typographyStyles.referralIntro}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Referral-card copy — authored desktop semibold/mobile regular scale. */
export const ReferralCardDescription = styled.p<TextProps>`
  ${typographyStyles.referralCardDescription}
  ${textBase}
  ${responsiveTextOverrides}
`;

// --- Bold / Semibold inline strong -----------------------------------------

/**
 * Bold inline <strong> — Body Medium Semibold at weight 700. Inherits color and
 * text-align from context (doesn't set them), so it composes anywhere.
 */
export const Bold = styled.strong<TextProps>`
  ${typographyStyles.bodyMediumSemibold}
  font-weight: ${FONT_WEIGHT.bold};
  ${textBase}
  ${textOverrides}
`;

/** Semibold inline — Figma "Body Medium Semibold" (600). Inherits color/alignment. */
export const Semibold = styled.strong<TextProps>`
  ${typographyStyles.bodyMediumSemibold}
  ${textBase}
  ${textOverrides}
`;

/** Semibold inline, small — Figma "Body Small Semibold". Inherits color/alignment. */
export const SemiboldSmall = styled.strong<TextProps>`
  ${typographyStyles.bodySmallSemibold}
  ${textBase}
  ${textOverrides}
`;

// --- Overline --------------------------------------------------------------

/**
 * Uppercase overline — Figma "Overline" (13px / 700 / uppercase / 6%
 * letter-spacing). Colour inherits from :root (COLOR_APP_TEXT) unless a `color`
 * prop is passed.
 */
export const Overline = styled.span<TextProps>`
  ${typographyStyles.overline}
  ${textOverrides}
`;
